import { Button, Field, Modal } from '@payaca/ambrosia';
import { FC, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { actions as jobActions } from '@/api/jobs';
import { CustomerSelect } from '@/ui/components/customerSelect/CustomerSelect';

export type DuplicatedProposal = {
  newJobId: number;
  projectPublicId: string;
  proposalPublicId: string;
};

type Props = {
  isOpen: boolean;
  jobId?: number;
  onClose: () => void;
  onDuplicated: (duplicated: DuplicatedProposal) => void;
  onError: (message: string) => void;
};

/**
 * Prompts for a Customer before duplicating a Proposal, so the duplicate can
 * never produce a Project with no Customer.
 */
export const DuplicateProposalModal: FC<Props> = (props) => {
  const { isOpen, jobId, onClose, onDuplicated, onError } = props;

  const dispatch = useDispatch();
  const [selectedCustomerId, setSelectedCustomerId] = useState<number>();
  const [isDuplicating, setIsDuplicating] = useState(false);

  const handleClose = useCallback(() => {
    if (isDuplicating) return;
    setSelectedCustomerId(undefined);
    onClose();
  }, [isDuplicating, onClose]);

  const handleDuplicate = useCallback(() => {
    if (!jobId || !selectedCustomerId) return;
    setIsDuplicating(true);
    dispatch(
      jobActions.duplicateJob(
        jobId,
        selectedCustomerId,
        (error: any, response: any) => {
          setIsDuplicating(false);
          if (error) {
            onError(
              error?.message ||
                'Could not duplicate this proposal. Please try again or contact support.'
            );
            return;
          }
          setSelectedCustomerId(undefined);
          onDuplicated(response);
        }
      )
    );
  }, [dispatch, jobId, selectedCustomerId, onDuplicated, onError]);

  return (
    <Modal
      isOpen={isOpen}
      title="Duplicate proposal"
      onClose={handleClose}
      size="sm"
    >
      <Modal.Body>
        <Field.Legacy>
          <Field.Label>Customer</Field.Label>
          <CustomerSelect
            customerId={selectedCustomerId}
            onChange={(customerId) => setSelectedCustomerId(customerId)}
          />
        </Field.Legacy>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isDuplicating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDuplicate}
            disabled={!selectedCustomerId}
            isProcessing={isDuplicating}
          >
            Duplicate
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};
